/*
 * Provider-aware "ships together" cross-sell for the cart drawer.
 * - Recommends only same-provider items (so they ship in one shipment).
 * - Adds via AJAX + Dawn's cart-drawer.renderContents (no full-page reload).
 * - Re-initialises on cartUpdate; Dawn re-renders the whole drawer (incl. this
 *   mount) on add/quantity change, so seed + providers are always fresh.
 */
class ShipsTogether {
  constructor(mount) {
    this.mount = mount;
    this.seed = mount.dataset.seed;
    this.providers = (mount.dataset.cartProviders || '').split(',').filter(Boolean);
    this.mount.addEventListener('submit', this.onAdd.bind(this));
    this.load();
  }

  async load() {
    if (!this.seed) { this.mount.hidden = true; return; }
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}recommendations/products?section_id=cart-ships-together&product_id=${this.seed}&intent=related&limit=8`;
    try {
      const res = await fetch(url);
      if (!res.ok) { this.mount.hidden = true; return; }
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const items = Array.from(doc.querySelectorAll('.ships-together__item'));
      const sameProvider = items.filter((li) => this.providers.includes(li.dataset.provider));
      const chosen = (sameProvider.length ? sameProvider : items).slice(0, 3);
      if (!chosen.length) { this.mount.hidden = true; return; }
      const ul = document.createElement('ul');
      ul.className = 'ships-together__list';
      ul.setAttribute('role', 'list');
      chosen.forEach((li) => ul.appendChild(li));
      this.mount.innerHTML = '<h3 class="ships-together__heading">You might also like</h3>';
      this.mount.appendChild(ul);
      this.mount.hidden = false;
    } catch (e) {
      this.mount.hidden = true;
    }
  }

  async onAdd(event) {
    const form = event.target.closest('.ships-together__form');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('button');
    const idInput = form.querySelector('[name="id"]');
    if (!idInput) return;
    if (button) { button.setAttribute('disabled', 'true'); button.classList.add('loading'); }

    const cartDrawer = document.querySelector('cart-drawer');
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const addUrl = (window.routes && window.routes.cart_add_url) || `${root}cart/add`;
    const body = { items: [{ id: Number(idInput.value), quantity: 1 }], sections_url: window.location.pathname };
    if (cartDrawer) body.sections = cartDrawer.getSectionsToRender().map((s) => s.id);

    const config = (typeof fetchConfig === 'function') ? fetchConfig('javascript') : {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
    };
    config.body = JSON.stringify(body);

    try {
      const res = await fetch(addUrl, config);
      const response = await res.json();
      if (response.status) { // 422 / sold out — leave the customer in place
        if (button) { button.removeAttribute('disabled'); button.classList.remove('loading'); }
        return;
      }
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(response); // renderContents re-opens the drawer + re-renders this mount
      }
      // Re-render our recommendations against the updated cart. renderContents
      // already refreshed the cart-icon-bubble, so no global cartUpdate publish is needed.
      initShipsTogether();
      // If the mount wasn't replaced (e.g. no drawer), re-enable the still-present button.
      if (button && document.contains(button)) { button.removeAttribute('disabled'); button.classList.remove('loading'); }
    } catch (e) {
      if (button) { button.removeAttribute('disabled'); button.classList.remove('loading'); }
    }
  }
}

function initShipsTogether() {
  document.querySelectorAll('.ships-together[data-seed]').forEach(mount => {
    if (!mount._stInit) { mount._stInit = true; new ShipsTogether(mount); }
  });
}

// Handle both: deferred on initial page load (DOMContentLoaded not yet fired)
// AND dynamically injected after AJAX add-to-cart when DOMContentLoaded already fired.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initShipsTogether);
} else {
  initShipsTogether();
}

if (window.subscribe && window.PUB_SUB_EVENTS) {
  // Re-render replaced the mount with fresh seed/providers; re-init on it.
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => initShipsTogether());
}
